import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import GoogleSignInButton from './GoogleSignInButton';
import AuthErrorMessage from './AuthErrorMessage';
import './AuthForms.css';
import './AuthErrorMessage.css';

interface LoginFormProps {
  onSuccess?: () => void;
}

/**
 * Formulaire de connexion
 */
const LoginForm: React.FC<LoginFormProps> = ({ onSuccess }) => {
  const { login, error, resetAuthError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | undefined>(undefined);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Réinitialiser les erreurs
    resetAuthError();
    setLocalError(null);
    setErrorCode(undefined);
    
    // Valider les champs
    if (!email.trim()) {
      setLocalError('Veuillez saisir votre email');
      return;
    }
    
    if (!password) {
      setLocalError('Veuillez saisir votre mot de passe');
      return;
    }
    
    try {
      setLoading(true);
      await login(email, password);
      
      // Appeler le callback de succès si fourni
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Erreur de connexion:', error);
      
      // Capturer à la fois le message et le code d'erreur
      setLocalError(error.message || 'Erreur de connexion');
      
      // Extraire le code d'erreur pour suggestions adaptées
      if (error.code) {
        console.log('Code d\'erreur d\'authentification:', error.code);
        setErrorCode(error.code);
      }
    } finally {
      setLoading(false);
    }
  };

  // Utiliser l'erreur du contexte d'authentification ou l'erreur locale
  const errorMessage = error?.message || localError;

  return (
    <div className="auth-form-container">
      <form className="auth-form" onSubmit={handleLogin}>
        <h2 className="auth-form-title">Connexion</h2>
        
        {/* Message d'erreur amélioré avec suggestions */}
        <AuthErrorMessage 
          error={errorMessage} 
          errorCode={errorCode} 
          onClose={() => {
            resetAuthError();
            setLocalError(null);
            setErrorCode(undefined);
          }}
        />
        
        {/* Bouton Google (mis en premier) */}
        <div className="auth-preferred-option">
          <p className="auth-option-label">Option recommandée</p>
          <GoogleSignInButton 
            disabled={loading} 
            text="Se connecter avec Google" 
            userType="user" 
          />
        </div>
        
        {/* Séparateur */}
        <div className="auth-form-separator">
          <span>OU</span>
        </div>
        
        {/* Titre section manuelle */}
        <p className="auth-section-title">Connexion avec email et mot de passe</p>
        
        {/* Champ email */}
        <div className="auth-form-group">
          <label htmlFor="email" className="auth-form-label">Email</label>
          <input
            id="email"
            type="email"
            className="auth-form-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Votre adresse email"
            disabled={loading}
          />
        </div>
        
        {/* Champ mot de passe */}
        <div className="auth-form-group">
          <label htmlFor="password" className="auth-form-label">Mot de passe</label>
          <input
            id="password"
            type="password"
            className="auth-form-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Votre mot de passe"
            disabled={loading}
          />
        </div>
        
        {/* Bouton de connexion */}
        <button 
          type="submit" 
          className="auth-form-button"
          disabled={loading}
        >
          {loading ? 'Connexion en cours...' : 'Se connecter avec email'}
        </button>
      </form>
    </div>
  );
};

export default LoginForm;
